import { HttpClient, HttpContext, HttpContextToken, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CheckinDisplayState, Display, NewDisplay, Room, TrialVisit } from 'src/app/models/checkin';
import { environment } from 'src/environments/environment';

/** Requests with this context set don't send the user to the login page on 401. */
export const NO_LOGIN_REDIRECT = new HttpContextToken<boolean>(() => false);

/**
 * Check-in displays (back office), the display page itself, trial lessons
 * and the list of plans/rooms.
 */
@Injectable({
  providedIn: 'root',
})
export class CheckinService {
  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
    'Authorization': `Bearer ${environment.apiKeyInternal}`
  });

  constructor(private http: HttpClient) {}

  public displays(): Observable<Display[]> {
    return this.http.post<Display[]>(`${environment.apiUrl}displays/list.php`, '', { headers: this.headers });
  }

  public addDisplay(label: string, pid: number): Observable<NewDisplay> {
    const body = new HttpParams().set('label', label).set('pid', pid);
    return this.http.post<NewDisplay>(`${environment.apiUrl}displays/add.php`, body.toString(), { headers: this.headers });
  }

  public revokeDisplay(id: number): Observable<{ result: boolean }> {
    const body = new HttpParams().set('id', id);
    return this.http.post<{ result: boolean }>(`${environment.apiUrl}displays/revoke.php`, body.toString(), { headers: this.headers });
  }

  /** The codes to show on the display of plan pid; needs the display token. */
  public displayState(pid: number, token: string): Observable<CheckinDisplayState> {
    return this.http.get<CheckinDisplayState>(`${environment.apiUrl}display/checkin.php`, {
      params: new HttpParams().set('pid', pid),
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` }),
      context: new HttpContext().set(NO_LOGIN_REDIRECT, true)
    });
  }

  public trials(): Observable<TrialVisit[]> {
    return this.http.post<TrialVisit[]>(`${environment.apiUrl}visits/pending.php`, '', { headers: this.headers });
  }

  /** All plans that have courses, with their room names, in display order. */
  public rooms(): Observable<Room[]> {
    type PlanCourse = { pid: number; room?: { name: string; sort: number } };
    return this.http.get<PlanCourse[]>(`${environment.apiUrl}plan/get.php`, { params: { pids: 'all' } }).pipe(
      map(courses => {
        const rooms = new Map<number, Room & { sort: number }>();
        for (const c of courses) {
          if (!rooms.has(c.pid)) {
            rooms.set(c.pid, { pid: c.pid, name: c.room?.name ?? `Plan ${c.pid}`, sort: c.room?.sort ?? c.pid });
          }
        }
        return [...rooms.values()]
          .sort((a, b) => a.sort - b.sort || a.pid - b.pid)
          .map(({ pid, name }) => ({ pid, name }));
      })
    );
  }
}
